/**
 * Festive Offer Pricing Utility
 *
 * ~80% of products get a "festive" selling price from the low tier (₹100/200/300/500)
 * ~20% get a higher price (₹700/1000/1500/2000)
 * Original prices are generated to make discounts look realistic.
 * All prices are GST-inclusive — no extra tax at checkout.
 */

export interface FestivePrice {
  sellingPrice: number;   // Final price customer pays (GST included)
  originalPrice: number;  // Crossed-out MRP
  discountPercent: number;
  amountSaved: number;
}

const LOW_TIER = [100, 200, 300, 500];
const HIGH_TIER = [500, 600];

// Deterministic seed-based number in [0, 1)
const seededRandom = (seed: number): number => {
  const x = Math.sin(seed + 1) * 10000;
  return x - Math.floor(x);
};

/**
 * Generate realistic original price:
 * For selling ₹100 → original ~₹200–₹350
 * For selling ₹200 → original ~₹350–₹550
 * For selling ₹300 → original ~₹499–₹750
 * For selling ₹500 → original ~₹799–₹1199
 * For selling ₹700 → original ~₹1099–₹1599
 * For selling ₹1000 → original ~₹1599–₹2499
 * For selling ₹1500 → original ~₹2499–₹3999
 * For selling ₹2000 → original ~₹2999–₹4999
 */
const originalPriceRanges: Record<number, [number, number]> = {
  100:  [199,  349],
  200:  [349,  549],
  300:  [499,  749],
  500:  [799,  1199],
  600:  [999,  1499],
};

// Round to nearest "nice" number (ends in 9, 49, 99, etc.)
const niceRound = (n: number): number => {
  if (n < 200) return Math.round(n / 50) * 50 - 1;
  if (n < 500) return Math.round(n / 50) * 50 - 1;
  if (n < 1000) return Math.round(n / 100) * 100 - 1;
  return Math.round(n / 100) * 100 - 1;
};

export const getFestivePrice = (productIndex: number): FestivePrice => {
  const r = seededRandom(productIndex * 7 + 3);

  // 80% chance low tier, 20% high tier
  const isHighTier = r > 0.8;
  const tier = isHighTier ? HIGH_TIER : LOW_TIER;
  const tierIndex = Math.floor(seededRandom(productIndex * 13 + 5) * tier.length);
  const sellingPrice = tier[tierIndex];

  const [minOrig, maxOrig] = originalPriceRanges[sellingPrice];
  const origRaw = minOrig + seededRandom(productIndex * 19 + 11) * (maxOrig - minOrig);
  const originalPrice = Math.max(sellingPrice + 50, niceRound(origRaw));

  const amountSaved = originalPrice - sellingPrice;
  const discountPercent = Math.round((amountSaved / originalPrice) * 100);

  return { sellingPrice, originalPrice, discountPercent, amountSaved };
};

/** Badge color based on discount percentage */
export const discountBadgeColor = (pct: number): string => {
  if (pct >= 70) return 'bg-red-600';
  if (pct >= 60) return 'bg-orange-500';
  if (pct >= 50) return 'bg-pink-600';
  if (pct >= 40) return 'bg-purple-600';
  return 'bg-green-600';
};
